import { CheckedState } from '@radix-ui/react-checkbox';
import React from 'react';
import { match } from 'ts-pattern';

import { Checkbox, tv } from '@eluve/components';
import {
  HStack,
  Tag,
  TagProps,
  VariantProps,
  legacyTextStyles,
} from '@eluve/components';

import { CodeType } from './MedicalCodeTypeBadge';
import { TooltipLabel } from './TooltipLabel';

export const styles = tv({
  slots: {
    container: 'relative rounded-lg border border-transparent p-2',
    content: 'min-w-0 flex-1 pr-2',
    frame: 'pointer-events-none absolute -inset-px rounded-lg',
  },
  variants: {
    clickable: {
      true: {
        container: 'cursor-pointer hover:bg-bg-secondary',
      },
    },
    type: {
      default: {
        container: 'bg-bg-tertiary',
        content: 'text-text-primary',
      },
      ghost: {
        container: '',
        content: 'text-text-primary',
      },
      outline: {
        container: 'border-border-primary',
        content: 'text-text-primary',
      },
      outlineDashed: {
        content: 'text-text-tertiary',
        frame: 'bg-dashed-border-recommendation',
      },
    },
    disabled: {
      true: {
        container: 'pointer-events-none border-border-primary bg-white',
        content: 'pointer-events-none text-text-primary',
      },
    },
  },
  compoundVariants: [
    {
      clickable: true,
      type: 'default',
      class: {
        container: 'hover:bg-border-primary',
      },
    },
  ],
  defaultVariants: {
    type: 'default',
  },
});

const getTagColor = (codeType: CodeType) =>
  match<CodeType, TagProps['color']>(codeType)
    .with('CPT', () => 'teal')
    .with('ICD_10', () => 'lightblue')
    .with('ICD_9', () => 'orange')
    .with('SNOMED', () => 'purple')
    .with('HCPCS', () => 'lightgreen')
    .exhaustive();

export type MedicalCodeProps = {
  codeType: CodeType;
  code: string;
  description: string;
  includeDescription?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  onClick?: () => void;
  type?: VariantProps<typeof styles>['type'];
  disabled?: boolean;
};

export const MedicalCodeTag = React.forwardRef<
  HTMLDivElement,
  { code: string; codeType: CodeType; disabled?: boolean }
>(({ code, codeType, disabled = false }, ref) => (
  <Tag
    label={code}
    color={disabled ? 'gray' : getTagColor(codeType)}
    ref={ref}
    subtle={false}
  />
));

export const MedicalCode = React.forwardRef<HTMLDivElement, MedicalCodeProps>(
  (
    {
      codeType,
      code,
      description,
      includeDescription = true,
      onClick,
      startAdornment,
      endAdornment,
      type,
      disabled = false,
    },
    ref,
  ) => {
    const { container, content, frame } = styles({
      clickable: Boolean(onClick) && !disabled,
      type,
      disabled,
    });

    return (
      <HStack
        align="center"
        className={container()}
        wFull={false}
        inline
        ref={ref}
        onClick={onClick}
        gap={3}
      >
        {startAdornment && startAdornment}

        <HStack gap={2}>
          <Tag
            label={code}
            size="xs"
            color={disabled ? 'gray' : getTagColor(codeType)}
            subtle={false}
          />

          <div
            className={legacyTextStyles.body({
              size: 's',
              className: content(),
            })}
          >
            <p>{`${includeDescription ? `${description}` : ''}`}</p>
          </div>
        </HStack>

        {endAdornment && endAdornment}

        <div className={frame()} />
      </HStack>
    );
  },
);
MedicalCode.displayName = 'MedicalCode';

export const MedicalCodeMultiSelect: React.FC<{
  onClick: () => void;
  selectedCount: number;
  totalCount: number;
}> = ({ onClick, selectedCount, totalCount }) => {
  if (totalCount <= 1) {
    return null;
  }

  const checked: CheckedState = match({ selectedCount, totalCount })
    .with({ selectedCount: 0 }, () => false)
    .with({ selectedCount: totalCount }, () => true)
    .otherwise(() => 'indeterminate');

  const type = checked === true ? 'outline' : 'outlineDashed';

  const { container, content, frame } = styles({ clickable: true, type });

  const label =
    `${checked === true ? 'Deselect' : 'Select'} ` +
    `${totalCount === 2 ? 'both' : `all ${totalCount}`}`;

  return (
    <TooltipLabel
      label={label}
      tooltipContentProps={{ align: 'start', className: 'hidden sm:block' }}
    >
      <HStack
        align="center"
        className={container()}
        gap={3}
        inline
        onClick={onClick}
        wFull={false}
      >
        <Checkbox checked={checked} className="m-1 mr-0 sm:mr-1" />
        <div
          className={legacyTextStyles.body({
            size: 's',
            className: content({ type: 'default', className: 'sm:hidden' }),
          })}
        >
          {label}
        </div>
        <div className={frame()} />
      </HStack>
    </TooltipLabel>
  );
};
